import React, { useState, useEffect } from 'react';
import { Calendar as CalendarIcon } from 'lucide-react';
import { api } from '../api';
import { Calendar } from '../components/ui/calendar';

const CalendarPage = () => {
  const [calendar, setCalendar] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCalendar();
  }, []);

  const loadCalendar = async () => {
    try {
      const response = await api.getAuctionCalendar();
      setCalendar(response.data);
    } catch (error) {
      console.error('Error loading calendar:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  const getAuctionsForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return calendar.filter(entry => {
      const entryDate = new Date(entry.auction_date).toISOString().split('T')[0];
      return entryDate === dateStr;
    });
  };

  const auctionsForSelected = getAuctionsForDate(selectedDate);

  return (
    <div className="min-h-screen py-8" data-testid="calendar-page">
      <div className="container mx-auto px-6 md:px-12">
        <div className="mb-8">
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4 font-heading">
            Auction Calendar
          </h1>
          <p className="text-base md:text-lg text-slate-600">
            View all upcoming property auctions across the UK
          </p>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <p className="text-slate-600">Loading calendar...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-1">
              <div className="bg-white border border-slate-200 rounded-sm p-6">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  className="rounded-sm border"
                  data-testid="auction-calendar"
                />
              </div>
            </div>

            <div className="lg:col-span-2">
              <div className="bg-white border border-slate-200 rounded-sm p-6">
                <h2 className="text-2xl font-semibold mb-4 font-heading">
                  Auctions on {formatDate(selectedDate)}
                </h2>

                {auctionsForSelected.length === 0 ? (
                  <p className="text-slate-600">No auctions scheduled for this date.</p>
                ) : (
                  <div className="space-y-4">
                    {auctionsForSelected.map((entry, idx) => (
                      <div 
                        key={idx} 
                        className="p-4 border border-slate-200 rounded-sm hover:border-amber-700 transition-colors"
                        data-testid={`auction-entry-${idx}`}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="font-semibold text-lg mb-1">
                              {entry.auction_house}
                            </h3>
                            <p className="text-sm text-slate-600">
                              {entry.property_count} properties available
                            </p>
                          </div>
                          <CalendarIcon className="h-5 w-5 text-[#F5A623]" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="mt-8">
                <h2 className="text-2xl font-semibold mb-4 font-heading">
                  All Upcoming Auctions
                </h2>
                <div className="bg-white border border-slate-200 rounded-sm">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-medium">Auction House</th>
                          <th className="px-4 py-3 text-left text-sm font-medium">Date</th>
                          <th className="px-4 py-3 text-left text-sm font-medium">Properties</th>
                        </tr>
                      </thead>
                      <tbody>
                        {calendar.slice(0, 20).map((entry, idx) => (
                          <tr key={idx} className="border-b border-slate-100 last:border-0" data-testid={`auction-row-${idx}`}>
                            <td className="px-4 py-3 text-sm">{entry.auction_house}</td>
                            <td className="px-4 py-3 text-sm">{formatDate(entry.auction_date)}</td>
                            <td className="px-4 py-3 text-sm">{entry.property_count}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CalendarPage;